import React, { useRef, useEffect } from 'react';
import { SectionInstance } from '../types';
import InlineEditText from '../InlineEditText';
import { sfCtaText, sfCtaUrl, sfHeadline, sfSub } from './sectionHelpers';

/** Extract YouTube video ID from watch, short, or embed URLs. Returns null if not YouTube. */
function parseYouTubeId(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtube.com')) {
      return u.searchParams.get('v') || u.pathname.split('/').pop() || null;
    }
    if (u.hostname === 'youtu.be') {
      return u.pathname.replace('/', '');
    }
  } catch {}
  return null;
}

const HeroVideo: React.FC<{ section: SectionInstance }> = ({ section }) => {
  const c = section.controls;
  const overlay = c.overlay_opacity || 'medium';
  const opacityMap: Record<string, number> = { light: 0.3, medium: 0.5, dark: 0.7 };
  const op = opacityMap[overlay] ?? 0.5;
  const videoUrl = (c.video_url || '').trim();
  const poster = (c.poster_image || c.fallback_image || '').trim();

  const youtubeId = videoUrl ? parseYouTubeId(videoUrl) : null;
  const isDirectVideo = videoUrl && !youtubeId;

  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.play().catch(() => {});
  }, [videoUrl]);

  const youtubeSrc = youtubeId
    ? `https://www.youtube.com/embed/${youtubeId}?autoplay=1&mute=1&loop=1&playlist=${youtubeId}&controls=0&showinfo=0&rel=0&disablekb=1&iv_load_policy=3&modestbranding=1`
    : null;

  return (
    <header style={{ position: 'relative', overflow: 'hidden', minHeight: 560, padding: '100px 24px', background: '#1e293b', display: 'flex', alignItems: 'center' }}>

      {/* YouTube iframe — scaled to cover, pointer-events off so it doesn't intercept clicks */}
      {youtubeSrc && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 0, overflow: 'hidden', pointerEvents: 'none' }}>
          <iframe
            src={youtubeSrc}
            allow="autoplay; encrypted-media"
            style={{
              position: 'absolute',
              top: '50%', left: '50%',
              width: '177.78vh',   /* 16:9 — fills height */
              minWidth: '100%',
              height: '56.25vw',  /* 16:9 — fills width */
              minHeight: '100%',
              transform: 'translate(-50%, -50%)',
              border: 'none',
            }}
            title="Background video"
          />
        </div>
      )}

      {/* Direct MP4 */}
      {isDirectVideo && (
        <video
          ref={videoRef}
          loop
          playsInline
          poster={poster || undefined}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}
        >
          <source src={videoUrl} type="video/mp4" />
        </video>
      )}

      {/* Poster fallback when no video */}
      {!videoUrl && poster && (
        <img src={poster} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }} />
      )}

      {/* Dark overlay */}
      <div style={{ position: 'absolute', inset: 0, background: `rgba(0,0,0,${op})`, zIndex: 1 }} />

      {/* Content */}
      <div style={{ position: 'relative', zIndex: 2, textAlign: 'center', color: '#fff', maxWidth: 800, margin: '0 auto', width: '100%' }}>
        <InlineEditText
          tag="h1"
          controlKey="headline"
          style={{ fontSize: '3.75rem', marginBottom: 16, color: '#fff', letterSpacing: '-0.025em', fontWeight: 800, lineHeight: 1.08 }}
        >
          {sfHeadline(c, 'Your Headline')}
        </InlineEditText>
        <InlineEditText
          tag="p"
          controlKey="subheadline"
          style={{ fontSize: '1.25rem', opacity: 0.9, marginBottom: 32 }}
        >
          {sfSub(c, 'Supporting subheadline text goes here')}
        </InlineEditText>
        {sfCtaText(c) && (
          <a href={sfCtaUrl(c)} className="sb-cta-btn" style={{
            display: 'inline-block', padding: '14px 32px', borderRadius: 'var(--brand-btn-radius, 6px)',
            background: 'var(--brand-primary, #2563eb)', color: '#fff', fontWeight: 600,
            textDecoration: 'none', boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}>
            {sfCtaText(c)}
          </a>
        )}
      </div>
    </header>
  );
};

export default HeroVideo;
